import type {
	DraggableProvided,
	DraggableStateSnapshot,
	DroppableProvided,
} from "react-beautiful-dnd";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import type { EventOccasion } from "../../../../shared/types/event.types";

const reorder = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
	const result = Array.from(list);
	const [removed] = result.splice(startIndex, 1);
	result.splice(endIndex, 0, removed);
	return result;
};

interface EventOccasionDndOrderProps {
	eventOccasions: EventOccasion[];
	setEventOccasions: (occasions: EventOccasion[]) => void;
	primaryColor?: string;
	primaryLightColor?: string;
}

const DraggableItem = ({
	occasion,
	index,
	primaryColor,
	primaryLightColor,
}: {
	occasion: EventOccasion;
	index: number;
	primaryColor: string;
	primaryLightColor: string;
}) => (
	<Draggable
		key={(occasion as any).id || (occasion as any).eventOccasionId}
		draggableId={String(
			(occasion as any).id || (occasion as any).eventOccasionId,
		)}
		index={index}
	>
		{(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
			<div
				ref={provided.innerRef}
				{...provided.draggableProps}
				{...provided.dragHandleProps}
				className="mb-4"
				style={{
					...provided.draggableProps.style,
				}}
			>
				<div
					className="flex items-center px-4 border rounded w-full cursor-grab active:cursor-grabbing"
					style={{
						borderColor: primaryColor,
						backgroundColor: snapshot.isDragging
							? "#f0f0f0"
							: primaryLightColor,
						color: primaryColor,
						opacity: snapshot.isDragging ? 0.8 : 1,
						boxShadow: snapshot.isDragging
							? "0 5px 10px rgba(0,0,0,0.15)"
							: "none",
					}}
				>
					<p className="p-4 font-bold whitespace-pre-wrap">
						{`${index + 1}）${occasion.title}`}
					</p>
				</div>
			</div>
		)}
	</Draggable>
);

const EventOccasionDndOrder = ({
	eventOccasions,
	setEventOccasions,
	primaryColor = "#1890ff",
	primaryLightColor = "#e6f7ff",
}: EventOccasionDndOrderProps) => {
	const onDragEnd = (result: any) => {
		if (!result.destination) {
			return;
		}

		const items = reorder(
			eventOccasions,
			result.source.index,
			result.destination.index,
		);

		setEventOccasions(items);
	};

	return (
		<div className="w-full">
			{eventOccasions && eventOccasions.length > 0 ? (
				<DragDropContext onDragEnd={onDragEnd}>
					<Droppable
						droppableId="event-occasions-droppable"
						direction="vertical"
					>
						{(provided: DroppableProvided) => (
							<div
								{...provided.droppableProps}
								ref={provided.innerRef}
								className="flex flex-col w-full"
							>
								{eventOccasions.map((occasion, index) => (
									<DraggableItem
										key={`event-occasion-${(occasion as any).id || (occasion as any).eventOccasionId}`}
										occasion={occasion}
										index={index}
										primaryColor={primaryColor}
										primaryLightColor={primaryLightColor}
									/>
								))}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			) : (
				<p className="text-center py-8">イベントがありません。</p>
			)}
		</div>
	);
};

export default EventOccasionDndOrder;
